import { useEffect, Component } from "react";
import "./App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./i18n/LanguageContext";
import { lenisRef, scrollToId } from "./lib/scroll";
import Grain from "./components/Grain";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Story from "./components/Story";
import Marquee from "./components/Marquee";
import HowItWorks from "./components/HowItWorks";
import Gallery from "./components/Gallery";
import HeidrunPage from "./components/HeidrunPage";
import WreathPage from "./components/WreathPage";
import DreamcatcherPage from "./components/DreamcatcherPage";
import LettersPage from "./components/LettersPage";
import StoriesTeaser from "./components/StoriesTeaser";
import ForCompanies from "./components/ForCompanies";
import CompaniesPage from "./components/CompaniesPage";
import ProcessPage from "./components/ProcessPage";
import CoCreationPage from "./components/CoCreationPage";
import Commission from "./components/Commission";
import Signup from "./components/Signup";
import Footer from "./components/Footer";
import AboutPage from "./components/AboutPage";

function ScrollManager() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    if (state && state.scrollTo) {
      const id = state.scrollTo;
      window.setTimeout(() => scrollToId(id), 80);
    } else {
      window.scrollTo(0, 0);
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [pathname, state]);
  return null;
}

function Home() {
  return (
    <>
      <Hero />
      <Story />
      <Marquee />
      <HowItWorks />
      <Gallery />
      <StoriesTeaser />
      <ForCompanies />
      <Commission />
      <Signup />
    </>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error(err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-40 text-center font-serif text-2xl text-espresso">
          Something came undone. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({
      duration: 1.35,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    lenisRef.current = lenis;
    let raf = requestAnimationFrame(function loop(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <LanguageProvider>
      <ErrorBoundary>
        <div className="relative min-h-screen bg-paper font-sans text-espresso antialiased">
          <BrowserRouter>
            <ScrollManager />
            <Grain />
            <Nav />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/heidrun" element={<HeidrunPage />} />
                <Route path="/wreath" element={<WreathPage />} />
                <Route path="/dreamcatcher" element={<DreamcatcherPage />} />
                <Route path="/letters" element={<LettersPage />} />
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/process" element={<ProcessPage />} />
                <Route path="/co-creation-001" element={<CoCreationPage />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </LanguageProvider>
  );
}

export default App;
